import React, { useEffect, useRef, useState } from 'react';
import Slider from 'react-slick';
import classNames from 'classnames';

import css from './restore.module.css';
import { Button, TextLink } from '@mm/components';
import questionIcon from 'assets/question-icon-blue.svg';
import slideOneSrc from 'assets/restore_1.jpg';
import slideTwoSrc from 'assets/restore_2.jpg';
import slideThreeSrc from 'assets/restore_3.jpg';
import slideFourSrc from 'assets/restore_4.jpg';

import { useSelector } from 'react-redux';
import { useResolutionInfo } from '@mm/hooks';
import { PrimaryToolTip } from '../../../../../common/components/PrimaryToolTip/PrimaryToolTip';
import { BotBuilderRoutes } from '../../../../../routes/bot-builder-routes';
import {
  activeChatbotIdSelector,
  activeFacebookPageIdSelector,
  selectActiveCompanyId,
} from '@mm/state/selectors/viewSelectors';
import { useHistory } from 'react-router-dom';
import { RightOutlined, LeftOutlined } from '@ant-design/icons';
import { useOverflow } from '@mm/hooks/src/use-overflow';

import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { selectPromotersByType } from '@mm/state/selectors/promoterSelector';
import { selectCurrentUserId } from '@mm/state/selectors/userSelector';
import LocalStorageService from '@mm/utils/src/local-storage-service';
import { FacebookAdsIntegrations } from '../Integrations/NativeIntegrations/components/FacebookAdsIntegrations/FacebookAdsIntegrations';
import { GoogleAdsIntegrations } from '../Integrations/NativeIntegrations/components/GoogleAdsIntegrations/GoogleAdsIntegrations';
import { selectCanUseGoogleAdsIntegration } from '@mm/state/selectors/googleAdsSelectors';

type RestoreProps = {
  className?: string;
};

interface Props {
  className?: string;
  children: React.ReactElement;
  guardContent: React.ReactNode;
  guardCondition: boolean;
  classNameArrow?: string;
  classNameContent?: string;
  classNameContainer?: string;
  position?: 'top' | 'bottom' | 'right';
}

export const TooltipGuard = ({
  className,
  children,
  classNameArrow,
  classNameContent,
  classNameContainer,
  position,
  guardContent,
  guardCondition,
}: Props) => {
  const { isDesktop } = useResolutionInfo();

  if (!guardCondition) return children;

  return (
    <PrimaryToolTip
      type="warning"
      useHover={isDesktop}
      position={position}
      align={isDesktop ? 'center' : 'start'}
      classNameArrow={classNameArrow}
      classNameContent={classNameContent}
      classNameContainer={classNameContainer}
      withoutLine
      anchorElement={
        <div className={classNames(className, 'guard--disabled')}>
          {children}
        </div>
      }
    >
      {guardContent}
    </PrimaryToolTip>
  );
};

const Arrow = (props: {
  className?: string;
  onClick?: () => void;
  prev?: boolean;
  next?: boolean;
}) => {
  const { className, onClick, prev, next } = props;
  return (
    <div
      className={classNames(className, css.arrow, {
        [css.prev]: prev,
        [css.next]: next,
      })}
      onClick={onClick}
    >
      {prev && <LeftOutlined />}
      {next && <RightOutlined />}
    </div>
  );
};

const RestoreSections = () => {
  const history = useHistory();
  const canUseGoogleAdsIntegration = useSelector(
    selectCanUseGoogleAdsIntegration,
  );

  useEffect(() => {
    history.replace({
      ...history.location,
      state: { options: 'facebook_ads_audience' },
    });
  }, []);

  return (
    <>
      <div className={css.section}>
        <FacebookAdsIntegrations />
      </div>
      {canUseGoogleAdsIntegration && (
        <div className={css.section}>
          <GoogleAdsIntegrations />
        </div>
      )}
    </>
  );
};

export const Restore = ({ className }: RestoreProps) => {
  const promoters = useSelector(state =>
    selectPromotersByType(state, 'getemails'),
  );

  const [promotersFetching, setPromotersFetching] = useState(false);

  const chatbotId = useSelector(activeChatbotIdSelector);
  const facebookPageId = useSelector(activeFacebookPageIdSelector);

  const history = useHistory();

  const containerClassName = classNames(className, css.container);

  const [idx, setIdx] = useState(0);

  const ref = useRef<HTMLDivElement>(null);

  const slickTrackEl = ref.current?.children[0]?.children[1];
  const { isOverflowX } = useOverflow(slickTrackEl);

  const sliderOpts = {
    className: css.slider,
    dots: true,
    infinite: true,
    draggable: false,
    swipe: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    variableWidth: false,
    afterChange: setIdx,
    nextArrow: <Arrow next />,
    prevArrow: <Arrow prev />,
  };

  const companyId = useSelector(selectActiveCompanyId);
  const currentUserId = useSelector(selectCurrentUserId);

  const [welcomeSeen, setWelcomeSeen] = useState(
    LocalStorageService.getItem(
      `${companyId}_${currentUserId}_restore_welcome_seen`,
    ),
  );

  const handleConnectAccountsClick = () => {
    LocalStorageService.setItem(
      `${companyId}_${currentUserId}_restore_welcome_seen`,
      '1',
    );
    setWelcomeSeen(true);
  };

  const renderSlides = () =>
    [slideThreeSrc, slideTwoSrc, slideOneSrc, slideFourSrc].map(v => (
      <div className={css.slide} key={v}>
        <img src={v} alt={v} />
      </div>
    ));

  const { retrieveAll } = usePromoterCRUDActions();

  useEffect(() => {
    (async () => {
      if (facebookPageId) {
        setPromotersFetching(true);
        try {
          if (chatbotId) {
            await retrieveAll(chatbotId, 'getemails');
          }
        } finally {
          setPromotersFetching(false);
        }
      }
    })();
  }, [facebookPageId, chatbotId]);

  return (
    <div className={containerClassName}>
      <h1 className={css.title}>
        Restore — Superpower your retargeting audiences
      </h1>
      <span className={css.subtitle}>
        Send more high intent data to ad platforms for retargeting
      </span>

      {welcomeSeen && promoters?.length ? (
        <RestoreSections />
      ) : (
        <>
          <TooltipGuard
            classNameContainer={css.popoverContainer}
            classNameArrow={css.popoverArrow}
            guardCondition={!promoters.length}
            guardContent={
              <div className={css.popover}>
                <TextLink
                  className={css.link}
                  sm
                  onClick={() =>
                    history.push(BotBuilderRoutes.xRay.getLink({ chatbotId }))
                  }
                >
                  Install X-Ray Pixel
                </TextLink>
                to proceed with Restore
              </div>
            }
          >
            <Button
              css={{ container: css.button }}
              disabled={!promoters.length || promotersFetching}
              onClick={handleConnectAccountsClick}
            >
              Restore Ad Audiences
            </Button>
          </TooltipGuard>

          <div className={css.bulletin}>
            <div className={css.bulletinHeader}>
              <div className={css.iconContainer}>
                <img
                  src={questionIcon}
                  className={css.iconImg}
                  alt={questionIcon}
                />
              </div>

              <span>What does Restore do?</span>
            </div>

            <ul>
              <li>Boost your retargeting audiences.</li>
              <li>Eliminate bot traffic.</li>
              <li>Track users across devices.</li>
            </ul>
          </div>

          <div
            className={classNames(css.slider, {
              [css.scrolled]: idx,
              [css.overflow]: isOverflowX,
            })}
          >
            <div className={css.list} ref={ref}>
              <Slider {...sliderOpts}>{renderSlides()}</Slider>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export const RestorePage = () => <Restore />;
